import { useState } from "react";
import CustomTextInput from "../../components/formComponents/CustomTextInput";
import CustomButton from "../../components/common/CustomButton";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { merchantAuthController } from "../../controllers/authController";
import { Turnstile, Honeypot, turnstileConfigured } from "../../components/Turnstile";

type Step = "email" | "otp" | "reset";

const ForgetPassword = () => {
	const navigate = useNavigate();
	const [step, setStep] = useState<Step>("email");
	const [email, setEmail] = useState("");
	const [otp, setOtp] = useState("");
	const [requestId, setRequestId] = useState("");
	const [password, setPassword] = useState("");
	const [confirm, setConfirm] = useState("");
	const [loading, setLoading] = useState(false);
	const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
	const [company, setCompany] = useState(""); // honeypot

	const apiError = (err: any, fallback: string) =>
		toast.error(err?.response?.data?.errors?.[0] || err?.response?.data?.message || fallback);

	const submitEmail = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!email) return toast.error("Please enter your email address.");
		if (company.trim() !== "") return; // honeypot
		if (turnstileConfigured && !turnstileToken)
			return toast.error("Please complete the verification challenge.");
		try {
			setLoading(true);
			const res = await merchantAuthController.forgotPassword({ email }, turnstileToken);
			const rid = res?.data?.data?.requestId;
			if (!rid) throw new Error("No requestId returned");
			setRequestId(rid);
			setStep("otp");
			toast.success(`We sent a 4-digit code to ${email}.`);
		} catch (err: any) {
			apiError(err, "Couldn't start password reset.");
		} finally {
			setLoading(false);
		}
	};

	const submitOtp = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!otp) return toast.error("Please enter the code.");
		try {
			setLoading(true);
			const res = await merchantAuthController.verifyForgotPasswordOtp({ requestId, otp });
			const rid = res?.data?.data?.requestId;
			if (rid) setRequestId(rid);
			setStep("reset");
		} catch (err: any) {
			apiError(err, "Invalid or expired code.");
		} finally {
			setLoading(false);
		}
	};

	const submitReset = async (e: React.FormEvent) => {
		e.preventDefault();
		if (password.length < 6) return toast.error("Password must be at least 6 characters.");
		if (password !== confirm) return toast.error("Passwords don't match.");
		try {
			setLoading(true);
			await merchantAuthController.resetPassword({ requestId, newPassword: password });
			toast.success("Password reset. Please log in.");
			navigate("/login");
		} catch (err: any) {
			apiError(err, "Couldn't reset your password.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="md:w-1/2 w-full flex justify-center items-center md:px-8 md:max-h-screen overflow-auto">
			<div className="p-6 md:p-8 flex flex-col justify-center items-center gap-3 md:gap-4 text-black text-center max-w-[480px] w-full bg-white md:bg-transparent rounded-2xl md:rounded-none shadow-lg md:shadow-none">
				<h1 className="text-xl md:text-4xl font-bold font-sk-modernist">Reset Password</h1>

				{step === "email" && (
					<>
						<p className="text-sm md:text-base font-sk-modernist text-styola-gray mb-1 md:mb-2 text-center">
							Enter your email address and we&apos;ll send you a code to reset your password.
						</p>
						<form onSubmit={submitEmail} className="w-full">
							<div className="flex flex-col gap-3 md:gap-4 w-full">
								<CustomTextInput
									type="email"
									placeholder="Email Address*"
									className="border border-gray-300 rounded-md font-sk-modernist"
									value={email}
									onChange={setEmail}
									autoFocus
									required
								/>
								<Honeypot value={company} onChange={setCompany} />
								<Turnstile onToken={setTurnstileToken} />
								<CustomButton
									variant="secondary"
									title="Send Reset Code"
									type="submit"
									className="w-full font-sk-modernist"
									isLoading={loading}
									isDisabled={turnstileConfigured && !turnstileToken}
								/>
							</div>
						</form>
					</>
				)}

				{step === "otp" && (
					<>
						<p className="text-sm md:text-base font-sk-modernist text-styola-gray mb-1 md:mb-2 text-center">
							Enter the 4-digit code we sent to {email}.
						</p>
						<form onSubmit={submitOtp} className="w-full">
							<div className="flex flex-col gap-3 md:gap-4 w-full">
								<CustomTextInput
									type="text"
									placeholder="4-digit code*"
									className="border border-gray-300 rounded-md font-sk-modernist text-center tracking-[0.4em]"
									value={otp}
									onChange={setOtp}
									autoFocus
									required
								/>
								<CustomButton
									variant="secondary"
									title="Verify Code"
									type="submit"
									className="w-full font-sk-modernist"
									isLoading={loading}
								/>
							</div>
						</form>
					</>
				)}

				{step === "reset" && (
					<>
						<p className="text-sm md:text-base font-sk-modernist text-styola-gray mb-1 md:mb-2 text-center">
							Choose a new password.
						</p>
						<form onSubmit={submitReset} className="w-full">
							<div className="flex flex-col gap-3 md:gap-4 w-full">
								<CustomTextInput
									type="password"
									placeholder="New password*"
									className="border border-gray-300 rounded-md font-sk-modernist"
									value={password}
									onChange={setPassword}
									autoFocus
									required
								/>
								<CustomTextInput
									type="password"
									placeholder="Confirm new password*"
									className="border border-gray-300 rounded-md font-sk-modernist"
									value={confirm}
									onChange={setConfirm}
									required
								/>
								<CustomButton
									variant="secondary"
									title="Reset Password"
									type="submit"
									className="w-full font-sk-modernist"
									isLoading={loading}
								/>
							</div>
						</form>
					</>
				)}
			</div>
		</div>
	);
};

export default ForgetPassword;
